import { ReactNode } from "react";
import Reveal from "@/components/Reveal";
import { useActiveSection, useInView, useScrollProgress } from "@/hooks/useInView";
import { cn } from "@/lib/utils";
import { useI18n } from "@/i18n/I18nProvider";

export interface SectionLink {
  id: string;
  title: string;
}

/** Hairline progress bar pinned under the header. */
export const ReadingProgress = () => {
  const progress = useScrollProgress();
  return (
    <div className="fixed top-20 inset-x-0 z-40 h-[2px] pointer-events-none" aria-hidden>
      <div
        className="h-full bg-primary origin-left shadow-[0_0_12px_hsl(var(--primary)/0.6)]"
        style={{ transform: `scaleX(${progress})` }}
      />
    </div>
  );
};

/** Numbered heading with a rule that draws itself in; optional action on the right. */
export const SectionHeading = ({
  title,
  action,
  as: Tag = "h2",
}: {
  title: string;
  action?: ReactNode;
  as?: "h1" | "h2";
}) => {
  const { ref, inView } = useInView<HTMLDivElement>();
  return (
    <div ref={ref} className="flex items-baseline gap-5 mb-10">
      <Tag className="font-display font-medium text-[2.2rem] md:text-[2.8rem] leading-tight tracking-tight">{title}</Tag>
      <span className={cn("rule-draw flex-1 h-px bg-border self-center", inView && "is-visible")} />
      {action}
    </div>
  );
};

/** Numbered article section whose rule draws itself in on first view. */
export const DetailSection = ({
  id,
  title,
  children,
  className,
}: {
  id: string;
  title: string;
  children: ReactNode;
  className?: string;
}) => (
  <section id={id} className={cn("scroll-mt-32", className)}>
    <SectionHeading title={title} />
    <Reveal>{children}</Reveal>
  </section>
);

/** Sticky "on this page" list that follows the reader. */
export const OnThisPage = ({ sections }: { sections: SectionLink[] }) => {
  const active = useActiveSection(sections.map((s) => s.id));
  const { m } = useI18n();
  if (sections.length < 2) return null;

  return (
    <nav aria-label={m.common.onThisPage} className="hidden lg:block">
      <p className="font-mono text-xs uppercase tracking-[0.12em] text-muted-foreground mb-4">{m.common.onThisPage}</p>
      <ul className="border-l border-border">
        {sections.map((s) => {
          const isActive = active === s.id;
          return (
            <li key={s.id}>
              <a
                href={`#${s.id}`}
                className={cn(
                  "toc-link relative flex items-baseline gap-3 py-1.5 pl-4 text-sm transition-colors duration-300",
                  isActive ? "text-foreground is-active" : "text-muted-foreground hover:text-foreground",
                )}
              >
                {s.title}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

/** Small uppercase label used inside cards and the sidebar. */
export const Eyebrow = ({ children, className }: { children: ReactNode; className?: string }) => (
  <p className={cn("font-mono text-xs uppercase tracking-[0.12em] text-muted-foreground", className)}>{children}</p>
);
